// Break out various statistics relative to various
// Sheet "ICE Fuel Mix"
import { createSelector } from 'reselect';

import {
  getNumProjectionYears,
} from '../../selectors';

import {
  simpleGrowthRateSeries
} from './../../../services/statistics';

interface IFuelMix {
  gasoline: number,
  diesel: number,
  cng: number
  electric: number
}

interface IFuelMixNoElectric {
  gasoline: number,
  diesel: number,
  cng: number
}

// cng = clean natural gas
const makeFuelMixNoElectric = (gasoline: number, diesel: number, cng: number ): IFuelMixNoElectric => ({ gasoline, diesel, cng });

// Delhi A1 (Far Right Table), L9:L29
// THIS HAS A FEW MAGIC NUMBERS FROM THE SPREADSHEET
// Most notably, they will be at 100% natural gas...
const BASE_FUEL_MIX_DELHI = {
  autoRickshaw: {
    commercial: makeFuelMixNoElectric(0, 0, 100),
  },
  car: {
    commercial: makeFuelMixNoElectric(18.1879864298543, 48.8485332269008, 32.9634803432449),
    private: makeFuelMixNoElectric(100 - (18.43 + 4.2), 18.43, 4.2),
  },
  publicTransit: {
    bus: makeFuelMixNoElectric(4.98526632092793, 2.21575766777089, 92.7989760113012),
  },
  twoWheel: {
    commercial: makeFuelMixNoElectric(100, 0, 0),
    private: makeFuelMixNoElectric(100, 0, 0),
  }
}

// // Keep items from Table B1: No Electric BAU, 2015
const BASE_FUEL_MIX_NO_ELECTRIC = {
  autoRickshaw: {
    commercial: makeFuelMixNoElectric(41, 42, 17),
  },
  car: {
    commercial: makeFuelMixNoElectric(0, 100 - 4.2, 4.2),
    private: makeFuelMixNoElectric(100 - (18.43 + 4.2), 18.43, 4.2),
  },
  publicTransit: {
    bus: makeFuelMixNoElectric(0, 98, 2),
  },
  twoWheel: { // make 2 twoWheel categories just in case
    commercial: makeFuelMixNoElectric(100, 0, 0),
    private: makeFuelMixNoElectric(100, 0, 0),
  }
}

const makeFuelMix = (gasoline: number, diesel: number, cng: number, electric: number): IFuelMix => ({ gasoline, diesel, cng, electric });

// TABLE A1, right side
const BASE_FUEL_MIX = {
  autoRickshaw: {
    commercial: makeFuelMix(38, 40, 17, 5),
  },
  car: {
    commercial: makeFuelMix(.0001, 90.7999, 4.2, 5),
    private: makeFuelMix(76.37, 18.43, 4.2, 1),
  },
  publicTransit: {
    bus: makeFuelMix(0.0001, 96.9999, 2, 1),
  },
  twoWheel: { // make 2 twoWheel categories just in case
    commercial: makeFuelMix(95, 0, 0, 5),
    private: makeFuelMix(95, 0, 0, 5),
  }
}

const makeFutureFuelMix = (mix: IFuelMix) => {
  const denominator = 100 - mix.electric;
  return {
    cng: 100 * mix.cng / denominator,
    diesel: 100 * mix.diesel / denominator,
    gasoline: 100 * mix.gasoline / denominator,
  }
};


// Projected items from Table B.1: No Electric BAU, 2030
const GOAL_FUEL_MIX = {
  autoRickshaw: {
    commercial: makeFutureFuelMix(BASE_FUEL_MIX.autoRickshaw.commercial),
  },
  car: {
    commercial: makeFutureFuelMix(BASE_FUEL_MIX.car.commercial),
    private: makeFutureFuelMix(BASE_FUEL_MIX.car.private),
  },
  publicTransit: {
    bus: makeFutureFuelMix(BASE_FUEL_MIX.publicTransit.bus),
  },
  twoWheel: { // make 2 twoWheel categories just in case
    commercial: makeFutureFuelMix(BASE_FUEL_MIX.twoWheel.commercial),
    private: makeFutureFuelMix(BASE_FUEL_MIX.twoWheel.private),
  }
}

// "Growth Rate" Table
const makeFuelGrowthRate = (startMix: IFuelMixNoElectric, endMix: IFuelMixNoElectric, numYears: number) => {
  return {
    cng: (endMix.cng - startMix.cng) / numYears,
    diesel: (endMix.diesel - startMix.diesel) / numYears,
    gasoline: (endMix.gasoline - startMix.gasoline) / numYears,
  }
}

const PROJECTION_YEARS = 15;

const GROWTH_RATE = {
  autoRickshaw: {
    commercial: makeFuelGrowthRate(BASE_FUEL_MIX_NO_ELECTRIC.autoRickshaw.commercial, GOAL_FUEL_MIX.autoRickshaw.commercial, PROJECTION_YEARS),
  },
  car: {
    commercial: makeFuelGrowthRate(BASE_FUEL_MIX_NO_ELECTRIC.car.commercial, GOAL_FUEL_MIX.car.commercial, PROJECTION_YEARS),
    private: makeFuelGrowthRate(BASE_FUEL_MIX_NO_ELECTRIC.car.private, GOAL_FUEL_MIX.car.private, PROJECTION_YEARS),
  },
  publicTransit: {
    bus: makeFuelGrowthRate(BASE_FUEL_MIX_NO_ELECTRIC.publicTransit.bus, GOAL_FUEL_MIX.publicTransit.bus, PROJECTION_YEARS),
  },
  twoWheel: { // make 2 twoWheel categories just in case
    commercial: makeFuelGrowthRate(BASE_FUEL_MIX_NO_ELECTRIC.twoWheel.commercial, GOAL_FUEL_MIX.twoWheel.commercial, PROJECTION_YEARS),
    private: makeFuelGrowthRate(BASE_FUEL_MIX_NO_ELECTRIC.twoWheel.private, GOAL_FUEL_MIX.twoWheel.private, PROJECTION_YEARS),
  }
}

const getIceProjectionSeries = (startMix: IFuelMixNoElectric, growthMix: IFuelMixNoElectric, numYears: number) => {
  return {
    cng: simpleGrowthRateSeries(startMix.cng, growthMix.cng, numYears),
    diesel: simpleGrowthRateSeries(startMix.diesel, growthMix.diesel, numYears),
    gasoline: simpleGrowthRateSeries(startMix.gasoline, growthMix.gasoline, numYears),
  }

}

// Keep data structures simple... make a thing for each combustion engine type (ICE: Diesel, Gasoline, CNG)
// Vehicle Fuel Mix (CNG Regulation): A:128
// WE don't seem to need the BAU table...
export const getFuelProportions = createSelector(
  [
    getNumProjectionYears
  ],
  (numYears) => {
    const base = BASE_FUEL_MIX_DELHI;
    const growth = GROWTH_RATE;
    const nullGrowth = {
      cng: 0,
      diesel: 0,
      gasoline: 0,
    }
    return {
      autoRickshaw: {
        commercial: getIceProjectionSeries(base.autoRickshaw.commercial, nullGrowth, numYears),
      },
      car: {
        commercial: getIceProjectionSeries(base.car.commercial, growth.car.commercial, numYears),
        private: getIceProjectionSeries(base.car.private, growth.car.private, numYears),
      },
      publicTransit: {
        bus: getIceProjectionSeries(base.publicTransit.bus, growth.publicTransit.bus, numYears),
      },
      twoWheel: { // just pick one, it doesn't really matter
        commercial: getIceProjectionSeries(base.twoWheel.commercial, nullGrowth, numYears),
        private: getIceProjectionSeries(base.twoWheel.private, nullGrowth, numYears),
      }
    };
  }
)


export const debugFuel = () => {
  console.log('BASE_FUEL_MIX', BASE_FUEL_MIX_NO_ELECTRIC);
  console.log('GOAL FUEL MIX', GOAL_FUEL_MIX);
  console.log('GROWTH_RATE Commercial', GROWTH_RATE.publicTransit);
}



// WEBPACK FOOTER //
// ./src/store/selectors/vehicleStock/byFuel.ts